import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Phone, MessageCircle, ShieldCheck, MapPin } from "lucide-react";
import { COMPANY, BRANCHES } from "@/lib/company";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildServiceListSchema } from "@/lib/schema";
import type { EnServiceMeta } from "@/content/en-services/meta.gen";
import logo from "@/assets/logo-mark-256.webp";

const TITLE =
  "Services in English — Notary, Translation, Legalization, Visas in Phuket | Thai Notary Law";
const DESC =
  "All legal services for expats and investors in Phuket — Notary Public, Certified Translation, MFA Legalization, Marriage Registration, Long-Term Visas, Police Clearance. Fixed pricing, English service, four offices.";

export const Route = createFileRoute("/en/services/")({
  component: EnServicesIndex,
  loader: async (): Promise<{ services: EnServiceMeta[] }> => {
    const { EN_SERVICES_META } = await import("@/content/en-services/meta");
    return { services: EN_SERVICES_META };
  },
  head: ({ loaderData }) => ({
    ...pageHeadForProvince({
      title: TITLE,
      description: DESC,
      path: "/en/services",
      locale: "en_US",
      alternates: [{ lang: "th", path: "/services" }],
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbSchema([
            { name: "Home", url: "/en" },
            { name: "Services", url: "/en/services" },
          ]),
        ),
      },
      ...(loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                buildServiceListSchema(
                  loaderData.services.map((s) => ({
                    name: s.h1,
                    description: s.metaDesc,
                    url: `/en/services/${s.slug}`,
                    priceFrom: s.priceFrom,
                  })),
                ),
              ),
            },
          ]
        : []),
    ],
  }),
});

function EnServicesIndex() {
  const { services } = Route.useLoaderData() as { services: EnServiceMeta[] };
  return (
    <div className="min-h-screen bg-background text-foreground" lang="en">
      <EnHeader />
      <section className="border-b border-border bg-gradient-to-br from-primary to-primary/80 py-16 text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-background/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold">
            <ShieldCheck className="h-3 w-3" /> Six core services · one licensed team
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-tight tracking-tight md:text-5xl">
            Legal &amp; Notarial Services in Phuket — designed for international clients.
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-primary-foreground/90">
            Each service is delivered by a registered Lawyers Council notarial attorney or a certified
            translator, with fixed pricing quoted before any work begins.
          </p>
        </div>
      </section>

      <section className="border-b border-border bg-background py-16">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-6 md:grid-cols-2">
            {services.map((s: EnServiceMeta) => (
              <Link
                key={s.slug}
                to="/en/services/$service"
                params={{ service: s.slug }}
                className="group flex flex-col justify-between rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-xl"
              >
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                    {s.heroEyebrow}
                  </div>
                  <h2 className="mt-2 text-xl font-extrabold tracking-tight text-primary group-hover:text-gold">
                    {s.h1}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.tldr}</p>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-xs">
                  <span className="font-semibold text-gold">
                    From ฿{s.priceFrom.toLocaleString()}{s.priceTo ? ` – ฿${s.priceTo.toLocaleString()}` : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground group-hover:text-gold">
                    View service <ArrowUpRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <EnOfficesStrip />
      <SiteFooter />
    </div>
  );
}

/* ---------- Shared English shell pieces (kept inline so /en/* stays self-contained) ---------- */

export function EnHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
        <Link to="/en" className="flex items-center gap-3">
          <img src={logo} alt={COMPANY.brandName} className="h-9 w-9" width="36" height="36" decoding="async" />
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">{COMPANY.brandName}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold">
              Phuket · English Desk
            </div>
          </div>
        </Link>
        <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
          <Link to="/en/services" className="hover:text-gold">
            Services
          </Link>
          <Link to="/en/insights" className="hover:text-gold">
            Insights
          </Link>
          <Link to="/en" hash="why" className="hover:text-gold">
            Why Us
          </Link>
          <Link to="/en" hash="offices" className="hover:text-gold">
            Offices
          </Link>
          <Link
            to="/"
            className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-accent"
          >
            ภาษาไทย
          </Link>
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            className="rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="mr-1 inline h-3 w-3" /> {COMPANY.phone}
          </a>
        </nav>
      </div>
    </header>
  );
}

export function EnCtaStrip() {
  return (
    <section className="bg-primary py-12 text-primary-foreground">
      <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-4 px-4 text-center md:flex-row md:text-left">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">Need this handled this week?</h2>
          <p className="mt-1 text-sm text-primary-foreground/80">
            Fixed price and timeline within 30 minutes during opening hours.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary hover:bg-gold/90"
          >
            <Phone className="h-3 w-3" /> {COMPANY.phone}
          </a>
          <a
            href="https://line.me/R/ti/p/@thainotary"
            className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-background/10"
          >
            <MessageCircle className="h-3 w-3" /> LINE @THAINOTARY
          </a>
        </div>
      </div>
    </section>
  );
}

export function EnOfficesStrip() {
  return (
    <section className="border-b border-border bg-muted/30 py-12">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-xs uppercase tracking-[0.2em] text-gold">Offices on Phuket</div>
        <h2 className="mt-2 text-2xl font-extrabold tracking-tight">Four locations · on-site island-wide</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {BRANCHES.map((b) => (
            <div key={b.id} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-gold">
                <MapPin className="h-4 w-4" />
                <div className="text-xs font-bold uppercase tracking-wider">{b.name}</div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{b.address}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}